// Shopify owns consent/delivery. Only empty, allowlisted funnel signals are published.
(() => {
  if (window.KiddoWonderFunnel) return;
  const names = new Set(['book_selected', 'preview_started', 'preview_generated', 'personalized_preview_viewed']);
  const seen = new Set();
  const watchers = new WeakMap();
  const emit = (name, key) => {
    try {
      if (!names.has(name)) return;
      const id = key ? name + ':' + key : null;
      if (id && seen.has(id)) return;
      const analytics = window.Shopify?.analytics;
      if (typeof analytics?.publish !== 'function') return;
      if (id) {
        seen.add(id);
        if (seen.size > 1000) seen.delete(seen.values().next().value);
      }
      Promise.resolve(analytics.publish('kiddowonder:' + name, {})).catch(() => {});
    } catch (_) { /* Measurement cannot interrupt a preview. */ }
  };
  const viewed = (image, key) => {
    try {
      if (!image || !key || !window.IntersectionObserver) return;
      watchers.get(image)?.();
      let visible = false;
      const cleanup = () => {
        observer.disconnect();
        image.removeEventListener('load', check);
        document.removeEventListener('visibilitychange', check);
      };
      const check = () => {
        if (visible && document.visibilityState === 'visible' && image.complete && image.naturalWidth > 0 && !image.hidden) {
          emit('personalized_preview_viewed', key);
          cleanup();
        }
      };
      const observer = new window.IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.25);
        check();
      }, { threshold: 0.25 });
      watchers.set(image, cleanup);
      image.addEventListener('load', check);
      document.addEventListener('visibilitychange', check);
      observer.observe(image);
    } catch (_) { /* Unsupported browser or observer must not affect the UI. */ }
  };
  window.KiddoWonderFunnel = { emit, viewed };
})();

(() => {
  'use strict';
  function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const find = (name) => root.querySelector(`[data-covers-${name}]`);
    const form = find('form');
    const base = (root.dataset.engineUrl || '').replace(/\/$/, '');
    const key = `kw_cover_gallery_v1_${base}`;
    let gallery = '', timer, failures = 0, photoUrl = '';
    const valid = (value) => /^[a-f0-9]{32}$/.test(value || '');
    const remember = () => { try { gallery ? sessionStorage.setItem(key, gallery) : sessionStorage.removeItem(key); } catch (_) {} };
    const open = () => { find('panel').hidden = false; find('open').setAttribute('aria-expanded', 'true'); };
    const error = (message = '') => { find('error').textContent = message; find('error').hidden = !message; };
    async function json(path, options = {}) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 45000);
      try {
        const response = await fetch(base + path, { ...options, cache: 'no-store', signal: controller.signal });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'We couldn’t load your covers. Please try again.');
        return data;
      } finally { clearTimeout(timeout); }
    }
    function showResults() {
      open(); form.hidden = true; find('results').hidden = false;
    }
    function render(data) {
      const total = data.items.length;
      const ready = data.items.filter((item) => item.status === 'ready').length;
      const failed = data.items.filter((item) => item.status === 'error').length;
      if (ready > 0) window.KiddoWonderFunnel?.emit('preview_generated', 'gallery:' + gallery);
      find('status').textContent = data.complete
        ? `${ready} of ${total} covers ready.${failed ? ` ${failed} couldn’t be created.` : ''}${ready ? ' Choose a cover to continue.' : ' Please start a new gallery to try again.'}`
        : `${ready} of ${total} covers ready. Choose any ready cover, or wait for more adventures…`;
      find('progress').max = total;
      find('progress').value = ready;
      find('help').hidden = data.complete;
      const grid = find('grid');
      // Preserve loaded images and keyboard focus across polling updates.
      data.items.forEach((item) => {
        let card = [...grid.children].find((node) => node.dataset.slug === item.slug);
        if (!card) {
          card = document.createElement('article'); card.className = 'kw-covers__card'; card.dataset.slug = item.slug;
          grid.append(card);
        }
        const signature = `${item.status}:${item.cover_url}:${item.batch}`;
        if (card.dataset.signature === signature) return;
        card.dataset.signature = signature;
        card.replaceChildren();
        if (item.cover_url) {
          const img = document.createElement('img'); img.src = item.cover_url; img.alt = `Your child’s ${item.label.toLowerCase()} cover`; card.append(img);
          window.KiddoWonderFunnel?.viewed(img, 'gallery:' + gallery);
        } else {
          const placeholder = document.createElement('div'); placeholder.className = 'kw-covers__placeholder';
          placeholder.textContent = item.status === 'error' ? 'Cover unavailable' : 'Creating your cover…'; card.append(placeholder);
        }
        const title = document.createElement('h3'); title.textContent = item.label; card.append(title);
        if (item.cover_url && valid(item.batch)) {
          const link = document.createElement('a'); link.className = 'kw-covers__button'; link.textContent = 'Choose this book';
          const path = root.dataset.previewUrl;
          const url = new URL(path, location.origin);
          if (path.includes('/custom-tale/preview/')) url.pathname += item.batch;
          else { url.searchParams.set('book', item.slug); url.searchParams.set('batch', item.batch); }
          link.href = url.href; link.setAttribute('aria-label', `Choose ${item.label}`);
          const image = card.querySelector('img');
          const imageLink = document.createElement('a'); imageLink.href = url.href;
          imageLink.setAttribute('aria-label', `Choose ${item.label}`); imageLink.tabIndex = -1;
          image.replaceWith(imageLink); imageLink.append(image); card.append(link);
        }
      });
      find('reset').hidden = !data.complete;
    }
    async function poll() {
      clearTimeout(timer);
      const current = gallery;
      find('resume').hidden = true;
      try {
        const data = await json(`/api/custom-tale/cover-gallery/${current}`);
        if (current !== gallery || !root.isConnected) return;
        failures = 0; error(); render(data);
        if (!data.complete) timer = setTimeout(poll, 5000);
      } catch (_) {
        if (current !== gallery || !root.isConnected) return;
        failures += 1;
        if (failures <= 5) {
          error('Connection interrupted. Reconnecting to your existing covers…');
          timer = setTimeout(poll, Math.min(15000, failures * 3000));
        } else {
          error('We couldn’t check progress. Your covers may still be generating. Check again to reconnect.');
          find('resume').hidden = false; find('reset').hidden = false;
        }
      }
    }
    find('open').addEventListener('click', () => { open(); find('panel').scrollIntoView({ behavior: 'smooth', block: 'start' }); });
    form.elements.reference.addEventListener('change', () => {
      if (photoUrl) URL.revokeObjectURL(photoUrl);
      const file = form.elements.reference.files[0];
      find('photo').hidden = !file;
      if (file) { photoUrl = URL.createObjectURL(file); find('photo').src = photoUrl; }
    });
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      const file = form.elements.reference.files[0];
      if (!file || file.size > 10 * 1024 * 1024) { error('Please choose a photo smaller than 10 MB.'); return; }
      const button = form.querySelector('[type="submit"]');
      button.disabled = true; button.textContent = 'Starting your covers…'; error();
      try {
        const body = new FormData(form);
        if (root.dataset.imageModel && root.dataset.imageModel !== 'default') body.set('image_model', root.dataset.imageModel);
        window.KiddoWonderFunnel?.emit('preview_started');
        const data = await json('/api/custom-tale/cover-gallery', { method: 'POST', body });
        if (!valid(data.gallery)) throw new Error('We couldn’t retrieve your gallery. Please try again.');
        gallery = data.gallery; remember(); showResults(); find('status').textContent = 'Preparing your 10 covers…';
        failures = 0; poll();
      } catch (exc) { error(exc.name === 'AbortError' ? 'The connection timed out while starting your gallery. Please try again.' : exc.message); }
      finally { button.disabled = false; button.textContent = 'Create my 10 free covers'; }
    });
    find('resume').addEventListener('click', () => { failures = 0; poll(); });
    find('reset').addEventListener('click', () => {
      clearTimeout(timer); gallery = ''; remember(); error();
      form.hidden = false; find('results').hidden = true; find('grid').replaceChildren(); find('reset').hidden = true;
    });
    try { gallery = sessionStorage.getItem(key) || ''; } catch (_) {}
    if (valid(gallery)) { showResults(); poll(); }
  }
  document.querySelectorAll('[data-cover-gallery]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-cover-gallery]').forEach(init));
})();
